import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Utensils,
  Dumbbell,
  Brain,
  Home as HomeIcon,
  TrendingUp,
  Activity as ActivityIcon,
  User,
  Droplets,
  Sparkles,
  Target,
  CheckCircle2,
  Info,
  Clock,
  Calendar,
  ChevronRight,
  RefreshCw,
  AlertCircle
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import CollapsibleSection from '../components/CollapsibleSection';
import BottomSheetModal from '../components/BottomSheetModal';
import GenerateProtocolModal from '../components/GenerateProtocolModal';
import { useProtocolProgress } from '../hooks/useProtocolProgress';

type ActivePage = 'home' | 'progress' | 'activity' | 'profile';

interface ProtocolViewProps {
  onBack: () => void;
  onNavigate: (page: ActivePage) => void;
}

interface ProtocolData {
  summary?: {
    motivation: string;
    key_focus_areas: string[];
    expected_biological_age_reduction: string;
    goal_type?: string;
    total_change_kg?: number;
    target_weight_kg?: number;
    starting_weight_kg?: number;
    weekly_change_kg?: number;
    timeline_weeks?: number;
    daily_calories?: number;
    success_metrics?: string[];
  };
  diet_plan?: {
    daily_calories: number;
    meal_suggestions: string[];
    water_glasses: number;
    notes: string;
    protein_g?: number;
    carbs_g?: number;
    fat_g?: number;
    fiber_g?: number;
    meal_timing?: string;
    foods_to_emphasize?: string[];
    foods_to_limit?: string[];
  };
  exercise_plan?: {
    phase1: {
      daily_activities: string[];
      duration_minutes: number;
      days: string;
      focus?: string;
      rest_days?: string[];
      weekly_goals?: string;
    };
    phase2: {
      daily_activities: string[];
      duration_minutes: number;
      days: string;
      focus?: string;
      rest_days?: string[];
      weekly_goals?: string;
    };
    phase3: {
      daily_activities: string[];
      duration_minutes: number;
      days: string;
      focus?: string;
      rest_days?: string[];
      weekly_goals?: string;
    };
    notes: string;
    intensity_level?: string;
    equipment_needed?: string[];
  };
  mental_exercises?: {
    selected_activities: string[];
    daily_duration_minutes: number;
    notes: string;
    schedule?: string;
    activity_breakdown?: Record<string, string>;
  };
  _calculation_metadata?: {
    bmr?: number;
    tdee?: number;
    warnings?: string[];
    was_adjusted?: boolean;
    daily_deficit?: number;
    daily_surplus?: number;
    original_timeline_weeks?: number;
  };
}

interface UserProtocol {
  id: string;
  protocol_generated: boolean;
  generation_date: string;
  protocol_data: ProtocolData;
  current_day: number;
  completed: boolean;
}

type ModalType = 'journey' | 'focus' | 'outcome' | 'diet' | 'exercise' | 'mental' | null;

export default function ProtocolView({ onBack, onNavigate }: ProtocolViewProps) {
  const [protocol, setProtocol] = useState<UserProtocol | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [daysSinceGeneration, setDaysSinceGeneration] = useState(0);

  // Calculate progress from generation date using custom hook
  const progress = useProtocolProgress(protocol?.generation_date ?? null, 21);

  useEffect(() => {
    loadProtocol();
  }, []);

  useEffect(() => {
    if (protocol && progress) {
      setSelectedWeek(progress.currentWeek);
      calculateDaysSinceGeneration();
    }
  }, [protocol, progress]);

  const calculateDaysSinceGeneration = () => {
    if (!protocol?.generation_date) return;
    
    const generationDate = new Date(protocol.generation_date);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - generationDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    setDaysSinceGeneration(diffDays);
  };

  const canRegenerate = daysSinceGeneration >= 5;

  const loadProtocol = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: protocolData } = await supabase
        .from('user_protocols')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (protocolData) {
        setProtocol(protocolData);
      }
    } catch (error) {
      console.error('Error loading protocol:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProtocolRegenerated = async (newProtocolData: ProtocolData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Update the existing protocol with new data
      const { data: updatedProtocol, error } = await supabase
        .from('user_protocols')
        .update({
          protocol_data: newProtocolData,
          generation_date: new Date().toISOString(),
          current_day: 1,
          completed: false,
          protocol_generated: true
        })
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating protocol:', error);
        return;
      }

      if (updatedProtocol) {
        setProtocol(updatedProtocol);
        setDaysSinceGeneration(0);
      }

      setShowRegenerateModal(false);
    } catch (error) {
      console.error('Error regenerating protocol:', error);
    }
  };

  const getPhaseData = (phase: number) => {
    if (!protocol?.protocol_data?.exercise_plan) return null;
    const phaseKey = `phase${phase}` as 'phase1' | 'phase2' | 'phase3';
    return protocol.protocol_data.exercise_plan[phaseKey];
  };

  const formatGenerationDate = () => {
    if (!protocol?.generation_date) return '';
    const date = new Date(protocol.generation_date);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!protocol || !protocol.protocol_generated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-10 h-10 text-gray-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">No Protocol Found</h2>
          <p className="text-gray-500 text-sm mb-6">Generate your personalized protocol to get started</p>
          <button
            onClick={onBack}
            className="px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const currentPhase = progress?.currentWeek ?? 1;
  const protocolData = protocol.protocol_data;
  const selectedPhaseData = getPhaseData(selectedWeek);

  const mealLabels = ['Breakfast', 'Lunch', 'Snack', 'Dinner'];

  const getWeekDays = (week: number) => {
    const startDay = (week - 1) * 7 + 1;
    const endDay = week * 7;
    return `Days ${startDay}-${endDay}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="max-w-2xl mx-auto">
        <header className="bg-white px-5 py-4 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-700" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold text-gray-900">21-Day Protocol</h1>
                <span className="px-2 py-0.5 bg-orange-100 rounded text-[10px] font-bold text-orange-600">AI</span>
              </div>
              <p className="text-xs text-gray-500">Day {progress?.currentDay ?? 1} of 21</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xl font-bold text-orange-500">{progress?.progressPercent ?? 0}%</div>
            <p className="text-[10px] text-gray-500">Complete</p>
          </div>
        </header>

        <div className="p-4 space-y-4">
          <div className="bg-white rounded-2xl p-4 border border-gray-200">
            <div className="flex gap-2">
              {[1, 2, 3].map((week) => {
                const isCurrentWeek = week === currentPhase;
                const isSelected = week === selectedWeek;

                return (
                  <button
                    key={week}
                    onClick={() => setSelectedWeek(week)}
                    className={`flex-1 py-3 px-4 rounded-xl transition-all ${
                      isSelected
                        ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="font-bold text-sm">Week {week}</span>
                        {isCurrentWeek && (
                          <div className={`w-1.5 h-1.5 rounded-full ${
                            isSelected ? 'bg-white' : 'bg-orange-500'
                          }`} />
                        )}
                      </div>
                      <span className={`text-[10px] ${
                        isSelected ? 'text-orange-100' : 'text-gray-500'
                      }`}>
                        {getWeekDays(week)}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {protocolData?.summary?.motivation && (
              <button
                onClick={() => setActiveModal('journey')}
                className="bg-white rounded-2xl p-4 border border-gray-200 text-left hover:border-cyan-300 hover:shadow-lg transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <Sparkles className="text-cyan-600" size={20} strokeWidth={2.5} />
                  <ChevronRight size={16} className="text-gray-400 group-hover:text-cyan-600 transition-colors" />
                </div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Your Journey</p>
                <p className="text-sm font-bold text-gray-900">View Story</p>
              </button>
            )}

            {protocolData?.summary?.key_focus_areas && protocolData.summary.key_focus_areas.length > 0 && (
              <button
                onClick={() => setActiveModal('focus')}
                className="bg-white rounded-2xl p-4 border border-gray-200 text-left hover:border-orange-300 hover:shadow-lg transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <Target className="text-orange-600" size={20} strokeWidth={2.5} />
                  <ChevronRight size={16} className="text-gray-400 group-hover:text-orange-600 transition-colors" />
                </div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Focus Areas</p>
                <p className="text-sm font-bold text-gray-900">{protocolData.summary.key_focus_areas.length} Goals</p>
              </button>
            )}

            {protocolData?.summary?.total_change_kg !== undefined && (
              <button
                onClick={() => setActiveModal('outcome')}
                className="bg-white rounded-2xl p-4 border border-gray-200 text-left hover:border-orange-300 hover:shadow-lg transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <Target className="text-orange-600" size={20} strokeWidth={2.5} />
                  <ChevronRight size={16} className="text-gray-400 group-hover:text-orange-600 transition-colors" />
                </div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Weight Target</p>
                <p className="text-xl font-bold text-gray-900">
                  {protocolData.summary.goal_type === 'weight_loss' 
                    ? `-${protocolData.summary.total_change_kg}kg`
                    : protocolData.summary.goal_type === 'weight_gain'
                    ? `+${protocolData.summary.total_change_kg}kg`
                    : 'Maintain'
                  }
                </p>
              </button>
            )}

            {protocolData?.summary?.expected_biological_age_reduction && (
              <button
                onClick={() => setActiveModal('outcome')}
                className="bg-white rounded-2xl p-4 border border-gray-200 text-left hover:border-green-300 hover:shadow-lg transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <TrendingUp className="text-green-600" size={20} strokeWidth={2.5} />
                  <ChevronRight size={16} className="text-gray-400 group-hover:text-green-600 transition-colors" />
                </div>
                <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Expected Outcome</p>
                <p className="text-sm font-bold text-gray-900">View Results</p>
              </button>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-gray-200">
            <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold px-5 pt-4 pb-3">Daily Plan</p>
            <div className="px-4 pb-4 space-y-3">
              {protocolData?.diet_plan && (
                <button
                  onClick={() => setActiveModal('diet')}
                  className="group w-full relative bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-xl p-4 text-left hover:shadow-lg transition-all overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                        <Utensils size={20} strokeWidth={2.5} />
                      </div>
                      <div>
                        <p className="text-sm font-bold mb-0.5">Diet Plan</p>
                        <p className="text-xs opacity-90">{protocolData.diet_plan.daily_calories} cal target</p>
                      </div>
                    </div>
                    <ChevronRight className="opacity-70" size={20} />
                  </div>
                </button>
              )}

              {protocolData?.exercise_plan && selectedPhaseData && (
                <button
                  onClick={() => setActiveModal('exercise')}
                  className="group w-full relative bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-4 text-left hover:shadow-lg transition-all overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                        <Dumbbell size={20} strokeWidth={2.5} />
                      </div>
                      <div>
                        <p className="text-sm font-bold mb-0.5">Exercise Plan</p>
                        <p className="text-xs opacity-90">{selectedPhaseData.duration_minutes} min/day</p>
                      </div>
                    </div>
                    <ChevronRight className="opacity-70" size={20} />
                  </div>
                </button>
              )}

              {protocolData?.mental_exercises && (
                <button
                  onClick={() => setActiveModal('mental')}
                  className="group w-full relative bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-4 text-left hover:shadow-lg transition-all overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-12 -mt-12"></div>
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                        <Brain size={20} strokeWidth={2.5} />
                      </div>
                      <div>
                        <p className="text-sm font-bold mb-0.5">Mental Wellness</p>
                        <p className="text-xs opacity-90">{protocolData.mental_exercises.daily_duration_minutes} min/day</p>
                      </div>
                    </div>
                    <ChevronRight className="opacity-70" size={20} />
                  </div>
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 bg-white rounded-2xl p-4 text-center border border-gray-200">
              <div className="text-2xl font-bold text-gray-900">{progress?.currentDay ?? 1}</div>
              <p className="text-xs text-gray-500">Current Day</p>
            </div>
            <div className="flex-1 bg-white rounded-2xl p-4 text-center border border-gray-200">
              <div className="text-2xl font-bold text-gray-900">{progress?.daysLeft ?? 21}</div>
              <p className="text-xs text-gray-500">Days Left</p>
            </div>
            <div className="flex-1 bg-orange-50 rounded-2xl p-4 text-center border border-gray-200">
              <div className="text-2xl font-bold text-orange-500">{progress?.progressPercent ?? 0}%</div>
              <p className="text-xs text-gray-500">Complete</p>
            </div>
          </div>

          {/* Regenerate Protocol Section */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <span className="text-xs text-gray-500">
                  Generated on {formatGenerationDate()}
                </span>
              </div>
              <span className="text-xs text-gray-400">
                {daysSinceGeneration} day{daysSinceGeneration !== 1 ? 's' : ''} ago
              </span>
            </div>

            {canRegenerate ? (
              <button
                onClick={() => setShowRegenerateModal(true)}
                className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-xl font-semibold transition-all shadow-lg"
              >
                <RefreshCw size={18} />
                <span>Regenerate Protocol</span>
              </button>
            ) : (
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <AlertCircle size={20} className="text-gray-400" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-700">
                    Regenerate available in {5 - daysSinceGeneration} day{5 - daysSinceGeneration !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-gray-500">
                    You can regenerate your protocol after 5 days
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-30">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-around">
            {[
              { id: 'home', icon: HomeIcon, label: 'Home' },
              { id: 'progress', icon: TrendingUp, label: 'Progress' },
              { id: 'activity', icon: ActivityIcon, label: 'Activity' },
              { id: 'profile', icon: User, label: 'Profile' }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id as ActivePage)}
                className="flex flex-col items-center gap-1 text-gray-400 hover:text-gray-600 transition-colors min-w-[60px]"
              >
                <item.icon size={22} strokeWidth={2} />
                <span className="text-[10px] font-bold">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>

      <BottomSheetModal
        isOpen={activeModal === 'journey'}
        onClose={() => setActiveModal(null)}
        title="Your Journey"
      >
        {protocolData?.summary?.motivation && (
          <div className="prose prose-sm max-w-none">
            <p className="text-gray-700 leading-relaxed">{protocolData.summary.motivation}</p>
          </div>
        )}
      </BottomSheetModal>

      <BottomSheetModal
        isOpen={activeModal === 'focus'}
        onClose={() => setActiveModal(null)}
        title="Key Focus Areas"
      >
        {protocolData?.summary?.key_focus_areas && (
          <div className="space-y-3">
            {protocolData.summary.key_focus_areas.map((area, index) => (
              <div key={index} className="flex items-start gap-3 p-3 bg-orange-50 rounded-xl">
                <div className="w-6 h-6 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle2 size={14} className="text-white" strokeWidth={3} />
                </div>
                <p className="text-sm text-gray-700 flex-1">{area}</p>
              </div>
            ))}
          </div>
        )}
      </BottomSheetModal>

      <BottomSheetModal
        isOpen={activeModal === 'outcome'}
        onClose={() => setActiveModal(null)}
        title="Expected Outcome"
      >
        <div className="space-y-4">
          {/* Weight Target Section */}
          {protocolData?.summary?.total_change_kg !== undefined && (
            <div className="p-4 bg-orange-50 rounded-xl border border-orange-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <Target className="text-orange-600" size={24} strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Weight Goal</p>
                  {protocolData.summary.goal_type === 'weight_loss' ? (
                    <>
                      <p className="text-lg font-bold text-gray-900">
                        Lose {protocolData.summary.total_change_kg} kg
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {protocolData.summary.starting_weight_kg} kg → {protocolData.summary.target_weight_kg} kg
                      </p>
                    </>
                  ) : protocolData.summary.goal_type === 'weight_gain' ? (
                    <>
                      <p className="text-lg font-bold text-gray-900">
                        Gain {protocolData.summary.total_change_kg} kg
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {protocolData.summary.starting_weight_kg} kg → {protocolData.summary.target_weight_kg} kg
                      </p>
                    </>
                  ) : (
                    <p className="text-lg font-bold text-gray-900">Maintain Current Weight</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Weekly Progress Section */}
          {protocolData?.summary?.weekly_change_kg !== undefined && (
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="text-blue-600" size={24} strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Weekly Target</p>
                  <p className="text-lg font-bold text-gray-900">
                    {protocolData.summary.weekly_change_kg} kg/week
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Estimated timeline: {protocolData.summary.timeline_weeks} weeks
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Biological Age Reduction Section */}
          {protocolData?.summary?.expected_biological_age_reduction && (
            <div className="p-4 bg-green-50 rounded-xl border border-green-200">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="text-green-600" size={24} strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-1">Biological Age</p>
                  <p className="text-gray-700 leading-relaxed">{protocolData.summary.expected_biological_age_reduction}</p>
                </div>
              </div>
            </div>
          )}

          {/* Motivational Note */}
          <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
            <div className="flex items-start gap-3">
              <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
              <p className="text-sm text-gray-600 leading-relaxed">
                These outcomes are estimated based on full compliance with your personalized protocol. Individual results may vary based on consistency and lifestyle factors.
              </p>
            </div>
          </div>
        </div>
      </BottomSheetModal>

      <BottomSheetModal
        isOpen={activeModal === 'diet'}
        onClose={() => setActiveModal(null)}
        title="Diet Plan"
      >
        {protocolData?.diet_plan && (
          <div className="space-y-4">
            {(protocolData.diet_plan.protein_g || protocolData.diet_plan.carbs_g || protocolData.diet_plan.fat_g) && (
              <div className="grid grid-cols-3 gap-3">
                {protocolData.diet_plan.protein_g && (
                  <div className="bg-blue-50 rounded-xl p-3 text-center">
                    <div className="text-xl font-bold text-gray-900">{protocolData.diet_plan.protein_g}g</div>
                    <p className="text-xs text-gray-600">Protein</p>
                  </div>
                )}
                {protocolData.diet_plan.carbs_g && (
                  <div className="bg-orange-50 rounded-xl p-3 text-center">
                    <div className="text-xl font-bold text-gray-900">{protocolData.diet_plan.carbs_g}g</div>
                    <p className="text-xs text-gray-600">Carbs</p>
                  </div>
                )}
                {protocolData.diet_plan.fat_g && (
                  <div className="bg-yellow-50 rounded-xl p-3 text-center">
                    <div className="text-xl font-bold text-gray-900">{protocolData.diet_plan.fat_g}g</div>
                    <p className="text-xs text-gray-600">Fat</p>
                  </div>
                )}
              </div>
            )}

            {protocolData.diet_plan.meal_timing && (
              <div className="border border-gray-200 rounded-xl">
                <CollapsibleSection
                  title="Meal Timing Guidelines"
                  icon={<Clock className="w-4 h-4 text-cyan-600" />}
                  variant="info"
                >
                  <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                    {protocolData.diet_plan.meal_timing}
                  </p>
                </CollapsibleSection>
              </div>
            )}

            {protocolData.diet_plan.notes && (
              <div className="border border-gray-200 rounded-xl">
                <CollapsibleSection
                  title="Nutrition Guidelines"
                  icon={<Info className="w-4 h-4 text-green-600" />}
                  variant="default"
                >
                  <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                    {protocolData.diet_plan.notes}
                  </p>
                </CollapsibleSection>
              </div>
            )}

            {protocolData.diet_plan.meal_suggestions && protocolData.diet_plan.meal_suggestions.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Daily Meals</h3>
                <div className="space-y-2">
                  {protocolData.diet_plan.meal_suggestions.map((meal, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-xl">
                      <p className="text-xs font-semibold text-gray-900 mb-1">{mealLabels[index]}</p>
                      <p className="text-sm text-gray-700">{meal}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="p-4 bg-blue-50 rounded-xl flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Droplets className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-semibold text-gray-900">Water Goal</span>
              </div>
              <span className="text-sm font-bold text-blue-600">
                {protocolData.diet_plan.water_glasses} glasses/day
              </span>
            </div>
          </div>
        )}
      </BottomSheetModal>

      <BottomSheetModal
        isOpen={activeModal === 'exercise'}
        onClose={() => setActiveModal(null)}
        title="Exercise Plan"
      >
        {protocolData?.exercise_plan && selectedPhaseData && (
          <div className="space-y-4">
            {selectedPhaseData.focus && (
              <div className="p-3 bg-orange-50 rounded-xl border border-orange-200">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-4 h-4 text-orange-600" strokeWidth={2} />
                  <span className="text-xs font-bold text-gray-900">Week {selectedWeek} Focus</span>
                </div>
                <p className="text-sm text-gray-700">{selectedPhaseData.focus}</p>
              </div>
            )}

            {selectedPhaseData.rest_days && selectedPhaseData.rest_days.length > 0 && (
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-xl border border-blue-200">
                <Calendar className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                <div className="flex-1">
                  <p className="text-xs font-bold text-gray-900 mb-1">Rest Days</p>
                  {selectedPhaseData.rest_days.map((restDay, idx) => (
                    <p key={idx} className="text-sm text-gray-700">{restDay}</p>
                  ))}
                </div>
              </div>
            )}

            {protocolData.exercise_plan.notes && (
              <div className="border border-gray-200 rounded-xl">
                <CollapsibleSection
                  title="Exercise Guidelines"
                  icon={<Info className="w-4 h-4 text-orange-600" />}
                  variant="warning"
                >
                  <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                    {protocolData.exercise_plan.notes}
                  </p>
                </CollapsibleSection>
              </div>
            )}

            {selectedPhaseData?.daily_activities && selectedPhaseData.daily_activities.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Daily Exercises</h3>
                <div className="space-y-2">
                  {selectedPhaseData.daily_activities.map((activity, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-orange-600">{index + 1}</span>
                        </div>
                        <p className="text-sm text-gray-700 flex-1">{activity}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </BottomSheetModal>

      <BottomSheetModal
        isOpen={activeModal === 'mental'}
        onClose={() => setActiveModal(null)}
        title="Mental Wellness"
      >
        {protocolData?.mental_exercises && (
          <div className="space-y-4">
            {protocolData.mental_exercises.schedule && (
              <div className="p-3 bg-purple-50 rounded-xl border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-purple-600" strokeWidth={2} />
                  <span className="text-xs font-bold text-gray-900">Recommended Schedule</span>
                </div>
                <p className="text-sm text-gray-700">{protocolData.mental_exercises.schedule}</p>
              </div>
            )}

            {protocolData.mental_exercises.notes && (
              <div className="border border-gray-200 rounded-xl">
                <CollapsibleSection
                  title="Mental Wellness Tips"
                  icon={<Info className="w-4 h-4 text-purple-600" />}
                  variant="default"
                >
                  <p className="text-sm text-gray-700 mt-2 leading-relaxed">
                    {protocolData.mental_exercises.notes}
                  </p>
                </CollapsibleSection>
              </div>
            )}

            {protocolData.mental_exercises.selected_activities && protocolData.mental_exercises.selected_activities.length > 0 && (
              <div>
                <h3 className="text-sm font-bold text-gray-900 mb-3">Daily Activities</h3>
                <div className="space-y-2">
                  {protocolData.mental_exercises.selected_activities.map((activity, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-bold text-purple-600">{index + 1}</span>
                        </div>
                        <p className="text-sm text-gray-700 flex-1">{activity}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </BottomSheetModal>

      {/* Regenerate Protocol Modal */}
      {showRegenerateModal && (
        <GenerateProtocolModal
          onComplete={handleProtocolRegenerated}
          onClose={() => setShowRegenerateModal(false)}
        />
      )}
    </div>
  );
} 
